/**
 * universal-video-decode.js — window.UniversalVideoDecode
 *
 * The decode orchestrator behind /video-player. Given ANY video File, it
 * returns a source a native <video> can actually play, picking the LIGHTEST
 * working path and degrading down a ladder:
 *
 *   Tier 0  native      — <video src=blob:>            (HW decode, 0 overhead)
 *   Tier 1  remux        — FFmpeg -c:v copy → fMP4 blob (repackage, no re-encode)
 *   Tier 3  transcode    — FFmpeg → H.264/AAC mp4       (universal floor, slow)
 *   (Phase B: streaming remux → MSE, and WebCodecs→canvas — added separately)
 *
 * Native and remux both hand a real file to <video>, so the BROWSER does A/V
 * sync — there is no hand-rolled sync clock to get wrong. The decision is
 * EMPIRICAL, not predicted: we actually try to play the file (and, after a
 * remux, the remuxed blob) and only fall further down the ladder on a real
 * failure. That self-corrects across the wild variance in per-browser codec
 * support (HEVC/AV1 in <video> depends on OS + hardware, which no static
 * table can know).
 *
 * Reuses the existing media stack — never reimplements it:
 *   window.FFmpegProcessor   (public/js/ffmpeg-processor.js) remux/transcode/probe
 *   window.CodecMuxRules     (public/js/codec-mux-rules.js) -c copy muxability
 *   window.CodecMatrix       (public/js/codec-matrix.js)    browser codec support
 *
 * The native fast path (Tier 0) touches NONE of the heavy WASM: a cheap
 * magic-byte sniff + an empirical <video> decode test. FFmpeg is loaded only
 * when the browser genuinely can't open the file — never for a plain MP4/WebM.
 *
 * Public API:
 *   UniversalVideoDecode.open(file, opts) -> Promise<Result>
 *     opts: { onProgress(stage, pct01, msg), audioIndex, signal,
 *             forceRemux, deferTranscode,
 *             transcode: { crf, preset, maxWidth } }   // tier-3 quality point;
 *               defaults are the playback copy (28/ultrafast/1920). A tool that
 *               re-encodes its own output FROM this proxy asks for crf 18.
 *               meta.scaledTo is set when the width cap actually applied.
 *     Result: { ok, tier, src, meta, rich, converted, convertedName,
 *               decoder, error, cleanup() }
 *   UniversalVideoDecode.probeRich(file)      -> Promise<Rich>   (tracks/subs/chapters)
 *   UniversalVideoDecode.extractSubtitle(file, relSubIndex) -> Promise<{url,text}>
 *   UniversalVideoDecode.canPlayNatively(fileOrBlob, timeoutMs) -> Promise<bool>
 *   UniversalVideoDecode.canPlayAudibly(srcOrBlob, timeoutMs)  -> Promise<bool>
 *       Same empirical spirit for SOUND: metadata, a real duration, and a
 *       clock that actually advances. Codec support is not container support.
 *
 * Never throws from open(): returns { ok:false, error } on total failure so
 * the tool can surface a localized message instead of a black screen.
 */
(function () {
'use strict';
if (typeof window === 'undefined') return;
if (window.UniversalVideoDecode && !window.UniversalVideoDecode.__isStub) return;

// Containers no browser has a demuxer for — skip the native attempt entirely
// (saves the ~100-300 ms <video> onerror dance / silent stall).
var NON_NATIVE = { avi: 1, wmv: 1, flv: 1, ts: 1 };
// Containers whose own index names the codecs, cheaply, via mp4box — the
// pre-verdict above reads these without touching FFmpeg. (Matroska is not
// here: mp4box cannot parse it, measured "parsed entire file without finding
// moov" on an 846 KB MKV.)
var MP4_FAMILY = { mp4: 1 };

// Video codecs we can REMUX into mp4 and expect a <video> to decode. h264 is
// universal; hevc/av1 are gated on the live browser matrix (and re-verified
// on the remuxed blob anyway). Everything else (mpeg4/xvid, vc1/wmv, mpeg1/2,
// theora, vp8/vp9-in-odd-containers) goes to transcode.
//
// Awaits CodecMatrix.ready() so the gate uses the DECODER's answer rather than
// the codec names the build advertises. The remux output is re-verified with
// canPlayNatively either way, so a wrong `yes` here was never handed to the user
// — it cost a whole wasted remux pass before falling through to transcode, which
// on a large file is minutes. Asking the right question up front is free.
function _matrix() {
    var m = { h265: false, av1: false, vp9: false, vp8: false };
    try {
        if (window.CodecMatrix && window.CodecMatrix.get) {
            window.CodecMatrix.get().forEach(function (r) {
                if (r.id === 'video.h265') m.h265 = (r.summary === 'supported');
                if (r.id === 'video.av1')  m.av1  = (r.summary === 'supported');
                if (r.id === 'video.vp9')  m.vp9  = (r.summary === 'supported');
                if (r.id === 'video.vp8')  m.vp8  = (r.summary === 'supported');
            });
        }
    } catch (_) {}
    return m;
}

function _matrixReady() {
    try {
        if (window.CodecMatrix && !window.CodecMatrix.__isStub && window.CodecMatrix.ready) {
            return window.CodecMatrix.ready().then(_matrix, _matrix);
        }
    } catch (_) {}
    return Promise.resolve(_matrix());
}

function _ext(file) {
    var n = (file && file.name) || '';
    var i = n.lastIndexOf('.');
    return i >= 0 ? n.slice(i + 1).toLowerCase() : '';
}

// Best-effort container from extension when the byte-sniff came back 'unknown'.
function _containerFromExt(file) {
    var e = _ext(file);
    if (e === 'mp4' || e === 'm4v') return 'mp4';
    if (e === 'mov' || e === '3gp' || e === '3g2') return 'mp4';
    if (e === 'webm') return 'webm';
    if (e === 'mkv') return 'webm'; // EBML family (sniff returns 'webm' for MKV too)
    if (e === 'ogv' || e === 'ogg') return 'ogg';
    if (e === 'avi') return 'avi';
    if (e === 'wmv' || e === 'asf') return 'wmv';
    if (e === 'flv') return 'flv';
    if (e === 'ts' || e === 'mts' || e === 'm2ts') return 'ts';
    return 'unknown';
}

// Cheap magic-byte container sniff (first 32 bytes) — routes the native-skip
// decision without loading anything. Mirrors video-probe.js's sniffer.
// Falls back to the extension when the signature is unrecognized.
async function _sniff(file) {
    if (!file) return 'unknown';
    if (file.size === 0) return 'empty';
    var b;
    try { b = new Uint8Array(await file.slice(0, 32).arrayBuffer()); }
    catch (_) { return _containerFromExt(file); }
    if (b.length < 4) return _containerFromExt(file);
    if (b.length >= 8 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return 'mp4'; // ftyp
    if (b.length >= 8) {
        var box = String.fromCharCode(b[4], b[5], b[6], b[7]);
        if (box === 'moov' || box === 'mdat' || box === 'wide' || box === 'free' || box === 'skip') return 'mp4';
    }
    if (b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3) return 'webm'; // EBML (mkv/webm)
    if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
        b[8] === 0x41 && b[9] === 0x56 && b[10] === 0x49 && b[11] === 0x20) return 'avi'; // RIFF..AVI
    if (b.length >= 8 && b[0] === 0x30 && b[1] === 0x26 && b[2] === 0xB2 && b[3] === 0x75 &&
        b[4] === 0x8E && b[5] === 0x66 && b[6] === 0xCF && b[7] === 0x11) return 'wmv'; // ASF
    if (b[0] === 0x46 && b[1] === 0x4C && b[2] === 0x56) return 'flv'; // FLV
    if (b.length >= 4 && b[0] === 0x4F && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) return 'ogg'; // OggS
    if (b[0] === 0x47 && (b[1] & 0x40)) return 'ts'; // MPEG-TS sync + PUSI
    return _containerFromExt(file);
}

// ── Deadlines in VISIBLE time ───────────────────────────────────────────
// Chromium does not load a media element in a hidden tab that has not played
// media yet — it waits for the tab to come back (DeferMediaLoad). Measured
// 23.09.2026 on real Chrome 153 and Yandex 26.8: while hidden, neither a blob
// <video> nor a MediaSource opens at all (20+ s, readyState 0), visible — 14 ms.
// A wall-clock deadline therefore read «the viewer switched tabs while the file
// was being checked» as «this file cannot play natively» and sent a perfectly
// playable file down the remux/transcode ladder. Every media deadline in this
// module counts visible time only; the shared primitive lives in
// timbrica-core.js. Without it (core failed to load) the old clock is used.
function _visibleDeadline(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var h = setTimeout(fn, ms);
    return function () { clearTimeout(h); };
}
function _visibleNow() {
    return (typeof window.__visibleNow === 'function') ? window.__visibleNow() : (window.performance || Date).now();
}

// ── Empirical native-playability test ───────────────────────────────────
// The ONLY reliable "the browser can actually play this" signal is a real
// frame reaching the compositor. `loadeddata` is NOT that: a browser fires it
// after merely PARSING the container (an Ogg/Theora or MKV/HEVC file the codec
// can't decode still gets metadata + a loadeddata event, then paints black
// with videoWidth === 0). So we drive playback (muted, no gesture needed) and
// wait for requestVideoFrameCallback — which fires only when a decoded frame
// with real pixels is presented. Falls back to loadeddata+videoWidth where
// rVFC is unavailable. This makes the tier choice self-correct across the wild
// variance in per-browser/-OS codec support (HEVC, AV1, Theora, VP9…).
//
// opts.expectDuration — seconds the source is known to last. A remux that
// «succeeded» with a fraction of the file is not a success: on the 22.09.2026
// report a 5.86 GB camera file came back from the whole-file remux as 341 MB
// with exit code 0. A result that plays but is shorter than 90 % of the source
// is refused here, so the ladder moves on instead of showing half a film.
function canPlayNatively(src, timeoutMs, opts) {
    opts = opts || {};
    return new Promise(function (resolve) {
        var url = null, revoke = false, v, done = false;
        try {
            if (typeof src === 'string') { url = src; }
            else { url = URL.createObjectURL(src); revoke = true; }
        } catch (_) { return resolve(false); }
        try { v = document.createElement('video'); } catch (_) { return resolve(false); }
        v.muted = true;
        v.defaultMuted = true;
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.preload = 'auto';
        var cancelDeadline = _visibleDeadline(function () { settle(false); }, timeoutMs || 6000);
        function settle(ok) {
            if (done) return; done = true;
            cancelDeadline();
            if (ok && opts.expectDuration > 0 && isFinite(v.duration) && v.duration > 0 &&
                v.duration < opts.expectDuration * 0.9) {
                ok = false;
                canPlayNatively.lastTruncated = { got: v.duration, want: opts.expectDuration };
            }
            try { v.pause(); } catch (_) {}
            try { v.removeAttribute('src'); v.load(); } catch (_) {}
            if (revoke) { try { URL.revokeObjectURL(url); } catch (_) {} }
            resolve(ok);
        }
        v.addEventListener('error', function () { settle(false); }, { once: true });
        if (typeof v.requestVideoFrameCallback === 'function') {
            v.requestVideoFrameCallback(function (now, meta) {
                settle(!!(meta && meta.width > 0));   // a real frame was composited
            });
        } else {
            // Older engines: loadeddata + a non-zero intrinsic size is the best
            // available proxy for "a frame decoded".
            v.addEventListener('loadeddata', function () { settle(v.videoWidth > 0); }, { once: true });
        }
        v.src = url;
        // Muted playback is allowed without a user gesture and forces the
        // decoder to actually produce a frame (so rVFC can fire).
        try { var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (_) {}
    });
}

// ── Empirical playability test for SOUND ────────────────────────────────
// The sibling of canPlayNatively(), and it exists for the same reason: the
// tables lie. MediaAudioProbe answers "can this browser decode this codec",
// which is a different question from "can this browser open this FILE" — a
// real 2002 QuickTime carrying plain MP3 (zlib-compressed `cmov` moov, from
// Cleaner 5) is refused outright with DEMUXER_ERROR_COULD_NOT_OPEN while the
// same MP3 in any other container plays. Codec support is not container
// support, and no table we keep will ever know every container.
//
// So drive the element and watch the clock: metadata with a real duration,
// no error, and currentTime actually advancing under muted playback.
function canPlayAudibly(src, timeoutMs) {
    return new Promise(function (resolve) {
        var url = null, revoke = false, v, done = false, started = 0;
        try {
            if (typeof src === 'string') { url = src; }
            else { url = URL.createObjectURL(src); revoke = true; }
        } catch (_) { return resolve(false); }
        try { v = document.createElement('video'); } catch (_) { return resolve(false); }
        v.muted = true; v.defaultMuted = true; v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.preload = 'auto';
        var cancelDeadline = _visibleDeadline(function () { settle(false); }, timeoutMs || 8000);
        function settle(ok) {
            if (done) return; done = true;
            cancelDeadline();
            try { v.pause(); v.removeAttribute('src'); v.load(); } catch (_) {}
            if (revoke) { try { URL.revokeObjectURL(url); } catch (_) {} }
            resolve(ok);
        }
        v.addEventListener('error', function () { settle(false); }, { once: true });
        v.addEventListener('loadedmetadata', function () {
            if (!isFinite(v.duration) || v.duration <= 0) return settle(false);
            started = v.currentTime;
            // Give playback a moment to actually move. A file the demuxer
            // accepted but cannot decode sits at 0 forever without erroring.
            // Visible time too: a background tab pauses muted playback.
            _visibleDeadline(function () { settle(v.currentTime > started + 0.01); }, 900);
        }, { once: true });
        v.src = url;
        try { var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (_) {}
    });
}

// ── Rich probe: enumerate audio/subtitle tracks + chapters from the ffmpeg
//    -i log (the only source that sees MKV/AVI internals). Needs FFmpeg
//    loaded; call only on the fallback path (or lazily for the tracks panel).
function _parseLog(log) {
    var out = {
        vcodec: '', acodec: '', duration: 0, width: 0, height: 0, rotation: 0,
        audioTracks: [], subTracks: [], chapters: []
    };
    if (!log) return out;
    var s = String(log);

    // Cut at the first Output section so passthrough "wrapped_avframe" rows
    // from `-f null -` never win (the codec-mux-rules _inputOnly trap).
    var cut = s.search(/^Output\s*#\d+/im);
    if (cut > 0) s = s.substring(0, cut);
    var lines = s.split('\n');

    var d = s.match(/Duration:\s*(\d+):(\d+):(\d+(?:\.\d+)?)/);
    if (d) out.duration = (+d[1]) * 3600 + (+d[2]) * 60 + parseFloat(d[3]);

    var aRel = 0, sRel = 0;
    var lastKind = '', lastRef = null;
    for (var i = 0; i < lines.length; i++) {
        var ln = lines[i];
        // Tolerate the MPEG-TS PID token: "Stream #0:0[0x100](eng): Video: h264".
        // Without the optional [..] group the PID broke the match → empty codec
        // → a remuxable H.264-in-TS wrongly fell to full transcode.
        var sm = ln.match(/Stream\s+#(\d+):(\d+)(?:\[[^\]]*\])?(?:\(([^)]+)\))?\s*:\s*(Video|Audio|Subtitle):\s*([a-z0-9_]+)/i);
        if (sm) {
            var global = parseInt(sm[2], 10);
            var lang = sm[3] || '';
            var kind = sm[4].toLowerCase();
            var codec = sm[5].toLowerCase();
            if (kind === 'video' && !out.vcodec) {
                out.vcodec = codec;
                var dim = ln.match(/(\d{2,5})x(\d{2,5})/);
                if (dim) { out.width = +dim[1]; out.height = +dim[2]; }
                /* What the DEVICE needs to know, not just the codec family: the
                   profile ("Main 10"), the pixel format (bit depth) and the frame
                   rate. "hevc" alone answered «can this browser decode HEVC» —
                   the codec matrix probes Main 8-bit at 1280x720 and said yes on
                   a machine whose GPU could not decode the 10-bit 4K file in
                   hand (#1726). The first (...) after the codec is the profile
                   unless it is the fourcc group "(hvc1 / 0x31637668)". */
                var pm = ln.match(/Video:\s*[a-z0-9_]+\s*\(([^)]*)\)/i);
                if (pm && !/\/\s*0x/i.test(pm[1])) out.vprofile = pm[1].trim();
                var pf = ln.match(/,\s*((?:yuvj?|nv|p0|gray|gbr|rgb|bgr|ya)[a-z0-9]*)\s*[(,]/i);
                if (pf) out.pixfmt = pf[1].toLowerCase();
                var fr = ln.match(/(\d+(?:\.\d+)?)\s*fps/i);
                if (fr) out.fps = parseFloat(fr[1]);
                lastKind = 'video'; lastRef = null;
            } else if (kind === 'audio') {
                if (!out.acodec) out.acodec = codec;
                var ch = 0;
                if (/\bmono\b/i.test(ln)) ch = 1;
                else if (/\bstereo\b/i.test(ln)) ch = 2;
                else { var cm = ln.match(/(\d+(?:\.\d+)?)\s*channels/i); if (cm) ch = parseFloat(cm[1]); }
                /* «5.1(side)», «7.1» — the layout people recognise from a disc
                   menu; the track list shows it next to the codec. */
                var lm = ln.match(/,\s*(\d\.\d)(?:\([a-z]+\))?\s*,/i);
                var layout = lm ? lm[1] : (ch === 1 ? '1.0' : ch === 2 ? '2.0' : '');
                if (lm && !ch) ch = (+lm[1].charAt(0)) + (+lm[1].charAt(2));
                /* Which track the browser plays on its own. Without it the tool
                   cannot tell "switch to Russian" from "switch back to what is
                   already playing" — and the second one must cost nothing. */
                var t = { relIndex: aRel++, streamIndex: global, codec: codec, lang: lang, title: '',
                          channels: ch, layout: layout, isDefault: /\(default\)/i.test(ln) };
                out.audioTracks.push(t);
                lastKind = 'audio'; lastRef = t;
            } else if (kind === 'subtitle') {
                var st = { relIndex: sRel++, streamIndex: global, codec: codec, lang: lang, title: '' };
                out.subTracks.push(st);
                lastKind = 'subtitle'; lastRef = st;
            }
            continue;
        }
        // Rotation side-data / metadata attached to the video stream.
        var rot = ln.match(/rotation of\s*(-?\d+(?:\.\d+)?)\s*degrees/i) || ln.match(/^\s*rotate\s*:\s*(-?\d+)/i);
        if (rot && !out.rotation) out.rotation = ((Math.round(parseFloat(rot[1])) % 360) + 360) % 360;
        // title metadata belongs to the most recent audio/subtitle stream.
        var tm = ln.match(/^\s*title\s*:\s*(.+?)\s*$/i);
        if (tm && lastRef) lastRef.title = tm[1];
    }

    // Chapters: "Chapter #0:0: start 0.000000, end 2.000000" then a title line.
    for (var j = 0; j < lines.length; j++) {
        var cmatch = lines[j].match(/Chapter\s+#\d+:\d+:\s*start\s+([\d.]+),\s*end\s+([\d.]+)/i);
        if (cmatch) {
            var title = '';
            for (var k = j + 1; k < Math.min(j + 4, lines.length); k++) {
                var tt = lines[k].match(/^\s*title\s*:\s*(.+?)\s*$/i);
                if (tt) { title = tt[1]; break; }
            }
            out.chapters.push({ start: parseFloat(cmatch[1]), end: parseFloat(cmatch[2]), title: title });
        }
    }
    /* Which camera made it. Only used to point at the low-resolution copy the
       camera already wrote next to this file (GoPro .LRV, DJI .LRF) when the
       original is too heavy for this device — the handler and encoder names are
       in the metadata ffmpeg prints anyway. */
    if (/GoPro/i.test(s)) out.camera = 'gopro';
    else if (/\bDJI\b|Osmo\s*Action/i.test(s)) out.camera = 'dji';
    else if (/Insta360/i.test(s)) out.camera = 'insta360';
    return out;
}

/* ── Is it the CODEC or the CONTAINER? ──────────────────────────────────
   After the native test fails, a remux is worth its cost only when the
   container was the problem. When the device cannot decode the video STREAM,
   rewrapping it changes nothing and on a big file costs minutes: 175 s on the
   5.86 GB action-camera file of 22.09.2026, which then failed the same way.

   The question goes to the decoder the element would use, with THIS stream's
   profile and size — not the codec matrix's generic probe (Main 8-bit, 720p),
   which answers «some HEVC decodes here». Measured on this project: Chrome
   and Yandex answer isConfigSupported() and <video> from the same decoder set
   (hardware HEVC present → both yes, disabled → both no, 23.09.2026).

   Only a plain avc1/hvc1/hev1/av01/vp09 string counts as a verdict. A Dolby
   Vision sample entry (dvh1) is refused by WebCodecs while <video> plays the
   HEVC base layer (measured on a dvh1-tagged file: isConfigSupported false,
   first frame in 125 ms) — for those the probe's profile is used instead. */
var PLAIN_CODEC = /^(avc[13]|hvc1|hev1|av01|vp09)\.[0-9A-Za-z.]+$/;
function _streamCodecFromProbe(p) {
    var w = p.width || 0, h = p.height || 0, fps = p.fps || 30, ps = w * h;
    var prof = String(p.vprofile || '').toLowerCase();
    var pix = String(p.pixfmt || '');
    var bits = /12(le|be)?$/.test(pix) ? 12 : (/10(le|be)?$|p010/.test(pix) ? 10 : 8);
    if (p.vcodec === 'hevc') {
        var hp = (/main 10/.test(prof) || bits === 10) ? '2.4' : (/rext|4:2:2|4:4:4/.test(prof) || bits > 10) ? '4.10' : '1.6';
        var lv = ps <= 983040 ? (fps > 30 ? 120 : 93)
               : ps <= 2228224 ? (fps > 30 ? 123 : 120)
               : ps <= 8912896 ? (fps > 60 ? 156 : fps > 30 ? 153 : 150)
               : (fps > 60 ? 186 : fps > 30 ? 183 : 180);
        return 'hvc1.' + hp + '.L' + lv + '.B0';
    }
    if (p.vcodec === 'h264') {
        var pp = /4:4:4/.test(prof) ? 'F4' : /4:2:2/.test(prof) ? '7A' : /high 10/.test(prof) ? '6E'
               : /high/.test(prof) ? '64' : /main/.test(prof) ? '4D' : /baseline/.test(prof) ? '42'
               : (bits > 8 ? '6E' : '64');
        var lh = ps <= 921600 ? (fps > 30 ? '20' : '1F')
               : ps <= 2228224 ? (fps > 30 ? '2A' : '28')
               : ps <= 8912896 ? (fps > 30 ? '34' : '33') : '3C';
        return 'avc1.' + pp + '00' + lh;
    }
    if (p.vcodec === 'av1') {
        var la = ps <= 2228224 ? (fps > 30 ? '09' : '08')
               : ps <= 8912896 ? (fps > 60 ? '14' : fps > 30 ? '13' : '12') : '17';
        return 'av01.0.' + la + 'M.' + (bits >= 10 ? '10' : '08');
    }
    if (p.vcodec === 'vp9') {
        var l9 = ps <= 2228224 ? (fps > 30 ? '41' : '40') : ps <= 8912896 ? (fps > 30 ? '51' : '50') : '61';
        return 'vp09.' + (bits >= 10 ? '02' : '00') + '.' + l9 + '.' + (bits >= 10 ? '10' : '08');
    }
    return '';
}
/* true / false / null (no verdict: no WebCodecs, no codec string, a hang). */
async function _deviceDecodes(codec, w, h) {
    if (!codec || typeof VideoDecoder === 'undefined' || typeof VideoDecoder.isConfigSupported !== 'function') return null;
    try {
        var cfg = { codec: codec };
        if (w > 0 && h > 0) { cfg.codedWidth = w; cfg.codedHeight = h; }
        var r = await Promise.race([
            VideoDecoder.isConfigSupported(cfg),
            new Promise(function (res) { setTimeout(function () { res(null); }, 3000); })
        ]);
        return r ? !!r.supported : null;
    } catch (_) { return null; }
}

function _hasStreams(log) {
    return /Stream\s+#\d+:\d+/i.test(String(log || ''));
}

async function probeRich(file) {
    var log = '';
    try {
        if (window.FFmpegProcessor) {
            if (!window.FFmpegProcessor.isReady()) await window.FFmpegProcessor.init();
            if (typeof window.FFmpegProcessor.probeStreaming === 'function') {
                try { log = await window.FFmpegProcessor.probeStreaming(file); } catch (_) {}
            }
            // Retry on the MEMFS transport when the streaming probe found no
            // STREAMS — not merely when it returned nothing. The two use
            // different file transports (WORKERFS mount vs a copy into the
            // heap), and the work path below takes MEMFS for anything under
            // 250 MB: a mount-specific failure used to return a non-empty
            // error log, skip this retry, and leave the caller transcoding
            // blind. Now it also decides a hard "unreadable" verdict, so the
            // second opinion has to be asked for.
            if (!_hasStreams(log) && typeof window.FFmpegProcessor.probe === 'function') {
                try {
                    var log2 = await window.FFmpegProcessor.probe(file);
                    if (_hasStreams(log2) || !log) log = log2;
                } catch (_) {}
            }
        }
    } catch (_) {}
    var parsed = _parseLog(log);
    parsed._log = log;
    return parsed;
}

// Extract embedded subtitle stream (relative index among subtitle streams)
// to WebVTT so it can drive a <track> / SubtitleOverlay.
/* Extract an embedded subtitle track to text.
 *
 * ⚠️ This is run AUTOMATICALLY the moment a file opens (autoPickSubtitle), so
 * its cost is paid by every viewer whether or not they wanted subtitles — and
 * FFmpegProcessor is ONE worker with no queue, so whatever it costs, the next
 * thing the viewer asks for waits that long. Measured on an 820 MB / 24-minute
 * MKV: 19–24 s of FFmpeg to produce 3 KB of text, and the first audio-track
 * switch after opening such a file took 23 s because it queued behind this.
 *
 * Lane 1 — Matroska, read in JavaScript (matroska-subs.js). The 20 s was never
 * I/O: reading the same 783 MB through file.slice() takes 0.56 s. Walking the
 * EBML here gives byte-identical cues in 0.02–2.2 s (9–18x faster, verified
 * cue-for-cue against FFmpeg on SRT, ASS and WebM-WebVTT sources) and never
 * touches the FFmpeg worker, so nothing queues behind it.
 *
 * Lane 2 — everything else keeps ONE FFmpeg pass.
 * ⚠️ It is deliberately NOT windowed, and that is a measured decision, not an
 * omission. Splitting the extraction into `-ss`-seeked windows works for audio
 * (see audio-track-sidecar.js) and it appeared to work for Matroska subtitles —
 * but on MP4/mov_text all three variants tried (shift-and-dedup, `-copyts`,
 * drop-the-edge-cue) returned the WRONG cues: 81 of 90 mismatched, with text
 * from 50 s appearing at 60 s. `-ss` does not place a subtitle stream where it
 * places an audio one. The earlier MKV run that looked clean only looked clean
 * because that fixture had one cue per minute and nothing straddled a seam.
 * A faster subtitle list that says the wrong words at the wrong time is not a
 * faster subtitle list.
 *
 * `expect` guards the hand-off between two numbering schemes: the JS reader
 * indexes its own track list, the probe indexes FFmpeg's. Same order in
 * practice — but a mismatch must fall through to FFmpeg, not hand back the
 * wrong language.
 */
function _sameLangTag(a, b) {
    a = String(a || '').toLowerCase(); b = String(b || '').toLowerCase();
    if (!a || !b || a === 'und' || b === 'und') return true;   // unknown proves nothing
    return a.slice(0, 2) === b.slice(0, 2);
}

async function extractSubtitle(file, relSubIndex, opts) {
    opts = opts || {};
    var rel = relSubIndex | 0;

    // ── lane 1: Matroska, in JS
    if (window.MatroskaSubs && !window.MatroskaSubs.__isStub) {
        var container = await _sniff(file);
        if (container === 'webm') {                     // _sniff reports 'webm' for MKV too
            var mk = null;
            try { mk = await window.MatroskaSubs.read(file, { signal: opts.signal, onProgress: opts.onProgress }); }
            catch (_) { mk = null; }
            if (mk && mk.ok && mk.tracks && mk.tracks[rel]) {
                var tr = mk.tracks[rel];
                if (tr.text && (!opts.expectLang || _sameLangTag(tr.lang, opts.expectLang))) {
                    return { text: tr.text, ext: tr.ext, source: 'matroska',
                             partial: !!mk.partial, ms: mk.ms, url: '', blob: null };
                }
            }
            /* Falling through is deliberate: a miss here means "the JS reader
               could not do it", never "this file has no subtitles". FFmpeg
               below is the second opinion. */
        }
    }

    // ── lane 2: one FFmpeg pass (see the warning above about windowing)
    if (!window.FFmpegProcessor) throw new Error('engine unavailable');
    if (!window.FFmpegProcessor.isReady()) await window.FFmpegProcessor.init();
    var out = await _run(file, ['-map', '0:s:' + rel, '-c:s', 'webvtt'], 'sub_' + rel + '.vtt');
    var text = '';
    try { text = await out.blob.text(); } catch (_) {}
    return { url: out.url, text: text, blob: out.blob, ext: 'vtt', source: 'ffmpeg', partial: false };
}

// Large files: WORKERFS-mount the input (constant-RAM, never copied into the
// WASM heap) via processStreaming, so a big source doesn't blow the heap before
// FFmpeg even starts. Small files keep process() (its reactive reinit/retry).
var STREAM_THRESHOLD = 250 * 1024 * 1024;
// Above this, whole-file FFmpeg remux risks the ~2 GB WASM output-heap ceiling
// (remux output ≈ input size). Route to libav.js streaming remux (bounded both
// ends: readahead input + OPFS streaming output) — no size ceiling.
//
// ⚠️ The 1 GB line is about MEMORY, not speed, and the temptation to lower it
// has been measured and REJECTED (2026-08-18, same page/browser/file, twice):
// on a 292 MB MKV/AAC — the case libav was supposed to win — FFmpeg finished in
// 6.2 s and 9.0 s against libav's 7.7 s and 15.1 s. An earlier "libav is 4.3x
// faster" reading was an apples-to-oranges mistake: it compared libav against
// FFmpeg's WORKERFS path, which production does not take below STREAM_THRESHOLD.
// Do not lower this without re-measuring the branch production actually runs.
var LIBAV_STREAM_THRESHOLD = 1024 * 1024 * 1024;   // 1 GB
// What the vendored build (variant-webcodecs) can actually OPEN. Measured on
// one small fixture per container: mp4 / mov / mkv remux and play; AVI, TS,
// FLV and WMV all fail instantly with "Could not open source file", WebM/VP8
// with "divide by zero". Without this gate a >1 GB AVI spent its libav attempt
// on a demuxer that never had a chance, then fell into exactly the whole-file
// FFmpeg remux the threshold exists to avoid.
var LIBAV_CONTAINERS = { mp4: 1, webm: 1 };        // _sniff() reports 'webm' for MKV too

/* ⚠️ NO `+faststart` here, and that is deliberate.
   `+faststart` rewrites the finished file a second time to move `moov` to the
   front. That matters for a file a SERVER streams over HTTP; it does nothing
   for a blob a local <video> reads, because a blob is fully seekable. Both
   consumers of this module — the player, and avatar-video-convert, which
   re-records res.src — only ever play the result locally, and the download
   offered beside the player is a file for a local player, where moov at the
   end is fine.

   Measured 2026-08-18, 292 MB H.264-in-AVI, three PAIRED end-to-end runs
   (time from picking the file to the first frame on screen):
       with faststart (shipped):  26.3 / 13.5 / 12.6 s   median 13.5
       without (this change):     18.8 / 10.9 / 11.6 s   median 11.6
   Faster in all three pairs, ~1.9 s at the median. Modest, and free — it is
   work with no consumer.

   ⚠️ An isolated in-page benchmark of the same four variants said 48 s → 33 s
   (−31%). That number was CONTAMINATED: feeding the file through the real
   uploader also starts the tool's own pipeline, so the benchmark ran against a
   second conversion of the same file. Trust the paired end-to-end figure.
   The same benchmark's `-c:a copy` column (another −24 s) is NOT taken:
   re-encoding MP3 to AAC for mp4 output is a deliberate compatibility decision
   (codec-mux-rules.js, 2026-08-02 — real players refuse MP3-in-MP4), and the
   audio actually arriving at this tier is overwhelmingly PCM, which has to be
   re-encoded regardless (measured over 60 d: pcm_* 80 sessions, no audio at
   all 93, mp3 only 9). */
var FASTSTART = [];
function _run(file, args, name, preInputArgs) {
    /* Both doors take preInputArgs in the SAME 5th slot; the 4th is the input
       name. Passing `-ss` as the 4th argument would silently become a filename
       and the seek would just not happen — the windowed subtitle extraction
       would then return the head of the file for every window. */
    if (file.size > STREAM_THRESHOLD && typeof window.FFmpegProcessor.processStreaming === 'function') {
        return window.FFmpegProcessor.processStreaming(file, args, name, undefined, preInputArgs);
    }
    return window.FFmpegProcessor.process(file, args, name, undefined, preInputArgs);
}

// ── FFmpeg remux (Tier 1): repackage into a faststart mp4 without re-encoding
//    video. Audio is copied when mp4-legal, else transcoded to AAC (video copy
//    still dominates, so this stays fast). Subs dropped (extracted separately).
//
//    forceAac: CodecMuxRules.AUDIO_COPY_OK.mp4 lists 'ac3'/'eac3' — correct for
//    the CONVERTER tools (AC-3-in-MP4 is ISO-legal and hardware players want it
//    kept), but wrong here: this remux exists to produce something THIS BROWSER
//    can play, and Chromium/Firefox refuse AC-3/E-AC-3/DTS/TrueHD outright
//    (measured — see media-audio-probe.js). Copying the track through produced
//    a picture with no sound, so the player forces a re-encode for those.
async function _remux(file, audioRelIndex, acodec, forceAac) {
    var audioArgs = forceAac
        ? ['-c:a', 'aac', '-b:a', '192k']
        : (window.CodecMuxRules
            ? window.CodecMuxRules.audioMuxArgs(acodec, 'mp4')
            : ['-c:a', 'aac', '-b:a', '128k']);
    var args = ['-map', '0:v:0', '-map', '0:a:' + (audioRelIndex | 0) + '?', '-c:v', 'copy']
        .concat(audioArgs)
        .concat(['-sn'].concat(FASTSTART));
    /* +genpts: an AVI (what DVRs and dashcams export) carries no packet timestamps, and a stream
       copy into mp4 then wrote packets with none («Timestamps are unset in a packet») — <video>
       sat at HAVE_METADATA and every seek waited out its timeout (H.264-in-AVI on
       /forensic-enhancer: minutes to the first frame, measured 30.10.2026). Inputs that have
       timestamps are left as they are. */
    return _run(file, args, 'remuxed.mp4', ['-fflags', '+genpts']);
}

// ── FFmpeg transcode (Tier 3): universal floor → H.264 High + AAC mp4.
//    yuv420p pinned (never High 10 → Windows can't open it). Source resolution
//    kept; a soft width cap bounds WASM memory / time on very large frames.
// preset/crf are a MEASURED choice, not a default (2026-08-05).
//
// Quality, SSIM against the source (1080p, native ffmpeg — quality is a
// property of the settings):
//     veryfast/23  0.99435      superfast/28 0.99490      ultrafast/28 0.99464
// So ultrafast/28 measures NO WORSE than the veryfast/23 it replaces.
//
// Speed, through this exact WASM build, presets INTERLEAVED over 3 rounds on
// one page and compared by median (see the warning below on why):
//     superfast/28  1.50x        ultrafast/28  2.59x     — ultrafast won 4/4 files
// Cost is size: ultrafast output is ~2x veryfast at equal SSIM. For a playback
// copy that is the right side of the trade — the file exists to be watched.
//
// ⚠ Benchmark honestly or not at all. Measuring the baseline, editing, then
// measuring again gave a CONFOUNDED answer: background load alone moved the
// same settings by 2x (1.31x vs 0.65x realtime), and one such pass "showed"
// wmv2 getting 2.7x slower under a setting that is in fact 3.3x faster.
// Only the interleaved run is evidence.
//
// ⚠ This does NOT make feature films viable. Even at 2.59x, a 90-minute 1080p
// film is ~2.3 h AND ~6 GB of output — past the readable-Blob ceiling. That
// needs progressive segmented playback, not a faster preset.
//
// `tx` (opts.transcode) lets a caller move the quality/speed point. The
// defaults above are right for a PLAYBACK copy. An editing tool that decodes
// this proxy frame by frame and re-encodes its own result from it is a
// different consumer: every artefact in the proxy becomes an artefact in the
// user's output, so it asks for crf 18 and accepts the bigger file. The width
// cap is exposed for the same reason — the cap is a memory/time bound, and the
// caller is the one who has to SAY on screen that the picture was reduced.
async function _transcode(file, audioRelIndex, width, tx) {
    tx = tx || {};
    var crf = tx.crf != null ? tx.crf : 28;
    var preset = tx.preset || 'ultrafast';
    var maxW = tx.maxWidth != null ? tx.maxWidth : 1920;
    var vArgs = window.CodecMuxRules
        ? window.CodecMuxRules.videoEncodeArgs('mp4', { crf: crf, preset: preset })
        : ['-c:v', 'libx264', '-preset', preset, '-crf', String(crf), '-pix_fmt', 'yuv420p'];
    var scale = [];
    if (maxW && width && width > maxW) scale = ['-vf', "scale='min(" + maxW + ",iw)':-2"];
    var args = ['-map', '0:v:0', '-map', '0:a:' + (audioRelIndex | 0) + '?']
        .concat(scale).concat(vArgs)
        .concat(['-c:a', 'aac', '-b:a', '160k', '-sn'].concat(FASTSTART));
    return _run(file, args, 'converted.mp4');
}


/* opts.intermediate — the caller only READS the proxy (a player source, frames
   for a model) and delivers something else. The page-wide createObjectURL
   tracker (telemetry-tier0.js) cannot tell: two seconds after the ladder makes
   a URL for its output it records `tool_complete{src:'blob'}` — a result for a
   pick nobody has processed yet — and sets its once-per-page flag, which also
   tells the abandonment probe the session HAD a result (and hides the real
   one from any tool that counts through the tracker alone). Prod analytics
   file (RU + intl) 09–23.09.2026, phantom `application/octet-stream`
   completions: /video-player 79 sessions, /video-blur 14, /remove-object 13,
   /video-bg-remover 2 (the day its RoSource ladder shipped). The blob is
   registered the moment a tier hands it over — before canPlayNatively makes
   its own URL — so the tracker's fire-time re-check skips it. Opt-in: on
   /video-player the conversion IS what the page counts today. */
function _notResult(opts, blob) {
    if (!opts || !opts.intermediate || !blob) return;
    try { if (typeof window.__skipBlobAutoTrack === 'function') window.__skipBlobAutoTrack(blob); } catch (_) {}
}

function _result(fields) {
    var urls = fields._urls || [];
    return {
        ok: fields.ok !== false,
        tier: fields.tier || 'native',
        src: fields.src || '',
        meta: fields.meta || {},
        rich: fields.rich || null,
        converted: fields.converted || null,       // Blob to download, if we transcoded/remuxed
        convertedName: fields.convertedName || '',
        decoder: fields.decoder || '',
        error: fields.error || null,
        cleanup: function () {
            urls.forEach(function (u) { try { URL.revokeObjectURL(u); } catch (_) {} });
            if (typeof fields._extraCleanup === 'function') { try { fields._extraCleanup(); } catch (_) {} }
        }
    };
}

async function open(file, opts) {
    opts = opts || {};
    var onProgress = opts.onProgress || function () {};
    var ffProgressHandler = null;
    function hookFfProgress(base, span) {
        ffProgressHandler = function (ev) {
            var p = (ev && typeof ev.detail === 'number') ? ev.detail : 0;
            onProgress('work', base + span * Math.max(0, Math.min(1, p)), '');
        };
        window.addEventListener('ffmpeg-progress', ffProgressHandler);
    }
    function unhookFfProgress() {
        if (ffProgressHandler) { try { window.removeEventListener('ffmpeg-progress', ffProgressHandler); } catch (_) {} ffProgressHandler = null; }
    }

    try {
        if (!file) return _result({ ok: false, error: 'no_file' });

        onProgress('probe', 0.05, 'probe');
        var container = await _sniff(file);
        if (container === 'empty') return _result({ ok: false, error: 'empty_file' });
        var base = { container: container, codec: '', acodec: '' };

        // ── Audio gate ──────────────────────────────────────────────────
        // canPlayNatively() proves a VIDEO FRAME was composited and runs the
        // probe MUTED — it is blind to sound. An H.264+AC-3 MKV (the standard
        // movie rip) therefore passed as `native` and played SILENTLY: no
        // error, no telemetry, the tool reported success. Read the container's
        // own audio codec id first; only a definite "this browser refuses it"
        // (playable === false) diverts to remux, so an unknown codec still
        // takes the old fast path.
        var audioInfo = null, forceAacAudio = false;
        if (window.MediaAudioProbe) {
            try { audioInfo = await window.MediaAudioProbe.probe(file); } catch (_) { audioInfo = null; }
            if (audioInfo && audioInfo.playable === false) {
                forceAacAudio = true;
                base.audioUnplayable = audioInfo.reason;
            }
            if (audioInfo && audioInfo.codecs && audioInfo.codecs.length) base.acodec = audioInfo.codecs[0];
        }

        // ── Tier 0: native (skip for containers no browser can demux, when
        //    the caller forces remux — e.g. selecting a non-default audio track,
        //    which native <video> cannot switch to reliably — and when the
        //    audio track would come out silent) ──
        // ── Cheap pre-verdict before the empirical probe ───────────────
        // canPlayNatively() is the honest test, but its cost is bounded only
        // by its own timeout: a file that neither errors nor presents a frame
        // just sits there. Measured on this build — QuickTime MJPEG and 3GP
        // H.263 both burned the FULL 6 000 ms before the ladder could even
        // start, while reading the codec out of the container answers in
        // ~70 ms. So when the container itself names a codec no browser
        // carries a decoder for, skip tier 0 outright. Only a DEFINITE `false`
        // short-circuits; `null`/`true` still go through the real probe, so
        // this can make the ladder faster and never wronger.
        var deadByCodec = false;
        var mbStreamCodec = '';     // the video sample entry's exact codec string (hvc1.2.4.H153.90)
        if (MP4_FAMILY[container] && window.CodecMuxRules && window.CodecMuxRules.videoPlaysInBrowsers) {
            try {
                var mbp = window.__Mp4BoxProbe;
                var pf = (typeof mbp === 'function') ? mbp(file)
                       : (mbp && typeof mbp.probe === 'function') ? mbp.probe(file, { timeoutMs: 4000 })
                       : null;
                if (pf) {
                    var mbInfo = await pf;
                    var mbTracks = (mbInfo && mbInfo.tracks) || [];
                    /* What the container itself lists besides the picture. One
                       sound and no text track means a player has no track list,
                       no subtitles and no chapter track to show — the caller can
                       skip the FFmpeg probe that would only confirm it. */
                    var nAud = 0, nText = 0;
                    for (var tj = 0; tj < mbTracks.length; tj++) {
                        var ty = mbTracks[tj] && mbTracks[tj].type;
                        if (ty === 'audio') nAud++;
                        else if (ty === 'subtitles' || ty === 'text') nText++;
                    }
                    base.mp4Tracks = { audio: nAud, text: nText };
                    for (var ti = 0; ti < mbTracks.length; ti++) {
                        var tc = String((mbTracks[ti] && mbTracks[ti].codec) || '');
                        if (mbTracks[ti] && (mbTracks[ti].video || /^(avc|hvc|hev|av01|vp09|dvh)/.test(tc))) { mbStreamCodec = tc; break; }
                    }
                    var mbCodec = window.CodecMuxRules.videoCodecFromMp4Box(mbTracks);
                    if (mbCodec && window.CodecMuxRules.videoPlaysInBrowsers(mbCodec) === false) {
                        deadByCodec = true;
                        base.codec = base.codec || mbCodec;
                    }
                }
            } catch (_) { /* probe unavailable or unreadable — fall through */ }
        }

        /* An audio codec this browser refuses is a reason to rebuild the SOUND,
           not the file. When the caller can play a separate audio track beside
           the picture (audio-track-sidecar.js), the picture is still worth
           trying natively: an H.264+AC-3 rip then opens instantly and gets its
           sound ~1 s later, instead of paying a whole-file remux — measured on
           an 820 MB / 24-min source, 45.6 s of remux against 1.9 s for one
           audio segment. Without such a caller nothing changes: forceAacAudio
           still diverts to the remux below. */
        var sidecarAudio = !!opts.audioSidecar && forceAacAudio;
        if (!NON_NATIVE[container] && !deadByCodec && !opts.forceRemux && (!forceAacAudio || sidecarAudio)) {
            onProgress('native', 0.12, 'native');
            /* How long the answer took, and how much of that the tab spent
               hidden — the caller reports both, so «the test failed while the
               viewer was elsewhere» stays visible in the data. */
            var nT0 = (window.performance || Date).now(), nV0 = _visibleNow();
            var ok0 = await canPlayNatively(file, 6000);
            base.nativeMs = Math.round((window.performance || Date).now() - nT0);
            base.nativeHiddenMs = Math.max(0, Math.round(base.nativeMs - (_visibleNow() - nV0)));
            base.nativeOk = !!ok0;
            if (ok0) {
                var url0 = URL.createObjectURL(file);
                if (sidecarAudio) base.audioSidecar = audioInfo && audioInfo.reason ? audioInfo.reason : 'codec';
                // The container probe already named the stream — the caller
                // can tell the viewer «H.264» without loading FFmpeg for it.
                if (mbStreamCodec) base.streamCodec = mbStreamCodec;
                return _result({
                    tier: 'native', src: url0, meta: base, decoder: 'browser-native',
                    _urls: [url0]
                });
            }
        }

        // ── Need FFmpeg from here. Load + rich probe (tracks/subs/chapters) ──
        onProgress('engine', 0.15, 'engine');
        if (!window.FFmpegProcessor) return _result({ ok: false, error: 'engine_unavailable', meta: base });
        if (!window.FFmpegProcessor.isReady()) await window.FFmpegProcessor.init();

        onProgress('inspect', 0.22, 'inspect');
        var rich = await probeRich(file);
        var v = rich.vcodec || '';
        var a = rich.acodec || '';
        if (rich.duration) base.duration = rich.duration;
        if (rich.width) { base.width = rich.width; base.height = rich.height; }
        if (rich.rotation) base.rotation = rich.rotation;
        if (rich.fps) base.fps = rich.fps;
        if (rich.pixfmt) base.pixfmt = rich.pixfmt;
        if (rich.vprofile) base.vprofile = rich.vprofile;
        if (rich.camera) base.camera = rich.camera;
        base.codec = v;
        base.acodec = a || base.acodec;
        // FFmpeg's own read of the audio codec is authoritative — let it
        // confirm (or discover) what the header sniff decided above.
        if (!forceAacAudio && a && window.MediaAudioProbe) {
            var vd = window.MediaAudioProbe._verdict([String(a).toLowerCase().replace(/^eac3$/, 'e-ac-3').replace(/^ac3$/, 'ac-3')]);
            if (vd.playable === false) { forceAacAudio = true; base.audioUnplayable = vd.reason; }
        }

        var audioRel = (opts.audioIndex != null) ? opts.audioIndex
                       : (rich.audioTracks[0] ? rich.audioTracks[0].relIndex : 0);

        // ── Verdict from the probe we ALREADY ran ────────────────────────
        // The probe knows, in ~1 s, when there is nothing here to play. Until
        // now that knowledge was thrown away: a file FFmpeg could not open at
        // all still walked into the transcode/progressive path, which mounted
        // a player that could never show a frame and never said why (measured
        // 465 sessions / 30 d ending on a black rectangle under a badge that
        // claimed playback; 220 of them a half-downloaded mp4 with no moov).
        //
        // Both verdicts are deliberately conservative and cross-checked
        // against the RAW log, not just the parsed fields: a codec token the
        // Stream regex misses must NOT be able to turn a playable file into a
        // hard verdict. If the log mentions a stream at all, we fall through
        // to the old behaviour.
        var _log = String(rich._log || '');
        var _sawAnyStream = /Stream\s+#\d+:\d+/i.test(_log) || /Duration:\s*\d/i.test(_log);
        var _sawVideoLine = /:\s*Video:/i.test(_log);
        if (!v && !_sawVideoLine) {
            if (!a && !rich.audioTracks.length && !rich.duration && !_sawAnyStream) {
                // Nothing parsed and nothing in the log: the container's index
                // is gone (interrupted recording / half-finished download).
                return _result({ ok: false, error: 'unreadable', meta: base, rich: rich });
            }
            if (rich.duration > 0 && (a || rich.audioTracks.length)) {
                // Sound but no picture. That is a real result, not a failure —
                // but it gets the SAME treatment as picture: prove it plays,
                // and convert it when it does not. Handing the raw blob over
                // on the strength of "the codec is mp3, mp3 is fine" is
                // exactly the shortcut that put a verdict card in front of a
                // perfectly good soundtrack (real file, 2026-08-18).
                base.audioOnly = true;
                onProgress('native', 0.3, 'native');
                var urlA = URL.createObjectURL(file);
                if (await canPlayAudibly(urlA, 8000)) {
                    return _result({
                        tier: 'audio-only', src: urlA, meta: base, rich: rich,
                        decoder: 'browser-native (audio only)', _urls: [urlA]
                    });
                }
                try { URL.revokeObjectURL(urlA); } catch (_) {}
                // The container is the problem, not the sound. Rewrap it.
                onProgress('remux', 0.4, 'remux');
                hookFfProgress(0.4, 0.5);
                try {
                    var copyLegal = !window.CodecMuxRules ||
                        window.CodecMuxRules.audioCopyOk(a, 'mp4') !== false;
                    var aArgs = ['-vn', '-map', '0:a:' + (audioRel | 0)]
                        .concat(copyLegal ? ['-c:a', 'copy'] : ['-c:a', 'aac', '-b:a', '192k'])
                        .concat(['-movflags', '+faststart']);
                    var ao = await _run(file, aArgs, _outName(file, 'm4a'));
                    _notResult(opts, ao && ao.blob);
                    unhookFfProgress();
                    if (await canPlayAudibly(ao.url, 8000)) {
                        return _result({
                            tier: 'audio-only', src: ao.url, meta: base, rich: rich,
                            converted: ao.blob, convertedName: _outName(file, 'm4a'),
                            decoder: 'browser-native (audio remux)', _urls: [ao.url]
                        });
                    }
                    try { URL.revokeObjectURL(ao.url); } catch (_) {}
                } catch (_) { unhookFfProgress(); }
                return _result({ ok: false, error: 'audio_unplayable', meta: base, rich: rich });
            }
        }

        // ── Tier 1: remux (browser-decodable codec in a non-native container) ──
        var m = await _matrixReady();
        /* The device's answer for THIS stream (see _deviceDecodes). `false` is
           the only value that changes anything: the remux is skipped, because
           rewrapping a stream the decoder refuses cannot make it play. */
        if (/^(h264|hevc|av1|vp9)$/.test(v)) {
            base.streamCodec = PLAIN_CODEC.test(mbStreamCodec) ? mbStreamCodec : _streamCodecFromProbe(rich);
            base.deviceDecodes = await _deviceDecodes(base.streamCodec, base.width, base.height);
        }
        var canRemux = ((v === 'h264') || (v === 'hevc' && m.h265) || (v === 'av1' && m.av1)) &&
                       base.deviceDecodes !== false;
        var muxOk = !window.CodecMuxRules || window.CodecMuxRules.videoCopyOk(v, 'mp4') === true;
        /* A whole-file FFmpeg remux keeps its OUTPUT in the wasm heap, and a
           copy's output is the size of its input. Above the layout's output
           wall (__outputFits, the one owner of that number) it cannot finish:
           measured on a 6.0 GB camera mp4, 67 s of work and then «RangeError:
           Array buffer allocation failed»; on the 22.09.2026 report it came back
           «successful» with 341 MB of a 5.86 GB file. The libav lane above
           streams to OPFS and is not bound by it, so only the FFmpeg rung is
           skipped — the ladder goes on to the progressive tier. */
        var remuxFits = (typeof window.__outputFits !== 'function') || window.__outputFits(file.size);
        if (canRemux && muxOk && !remuxFits) base.remuxSkipped = 'output_ceiling';
        if (!canRemux && base.deviceDecodes === false) base.remuxSkipped = 'device_cannot_decode';
        if (canRemux && muxOk) {
            // Large files (>1 GB): stream-remux via libav.js — bounded memory,
            // no size ceiling (whole-file FFmpeg remux would OOM the WASM heap).
            // Only when NOT selecting a specific audio track (libav copies all
            // streams) and the audio codec is mp4-copy-legal.
            // libav copies EVERY stream through, so it cannot rescue an audio
            // track this browser refuses — that would just carry the silence
            // into the output. Those files go to the FFmpeg remux below, which
            // can re-encode the audio while still copying the video.
            var audioLegal = !forceAacAudio &&
                (!window.CodecMuxRules || window.CodecMuxRules.audioCopyOk(a, 'mp4') !== false);
            var libavThreshold = (typeof window !== 'undefined' && window.__VP_LIBAV_THRESHOLD) || LIBAV_STREAM_THRESHOLD;
            if (file.size > libavThreshold && LIBAV_CONTAINERS[container] && opts.audioIndex == null &&
                window.LibavRemux && window.LibavRemux.isSupported() && audioLegal) {
                onProgress('stream', 0.3, 'stream-remux');
                try {
                    var sr = await window.LibavRemux.remuxToPlayable(file, {
                        onProgress: function (p) { onProgress('work', 0.3 + 0.6 * p, ''); },
                        signal: opts.signal
                    });
                    _notResult(opts, sr && sr.blob);
                    onProgress('verify', 0.92, 'verify');
                    canPlayNatively.lastTruncated = null;
                    if (await canPlayNatively(sr.blob, 8000, { expectDuration: rich.duration })) {
                        return _result({
                            tier: 'stream-remux', src: sr.url, meta: base, rich: rich,
                            converted: sr.blob, convertedName: _outName(file, 'mp4'),
                            decoder: 'browser-native (streamed remux)',
                            _urls: [sr.url], _extraCleanup: sr.cleanup
                        });
                    }
                    if (canPlayNatively.lastTruncated) base.remuxTruncated = canPlayNatively.lastTruncated;
                    sr.cleanup();
                } catch (e) { /* fall through to FFmpeg remux / transcode */ }
            }
            if (remuxFits) {
                onProgress('remux', 0.3, 'remux');
                hookFfProgress(0.3, 0.5);
                try {
                    var rmx = await _remux(file, audioRel, a, forceAacAudio);
                    _notResult(opts, rmx && rmx.blob);
                    unhookFfProgress();
                    onProgress('verify', 0.82, 'verify');
                    canPlayNatively.lastTruncated = null;
                    if (await canPlayNatively(rmx.blob, 6000, { expectDuration: rich.duration })) {
                        return _result({
                            tier: 'remux', src: rmx.url, meta: base, rich: rich,
                            converted: rmx.blob, convertedName: _outName(file, 'mp4'),
                            decoder: 'browser-native (remux)', _urls: [rmx.url]
                        });
                    }
                    // remuxed blob still won't decode (browser lied about the
                    // codec), or it plays but lost the tail of the file
                    if (canPlayNatively.lastTruncated) base.remuxTruncated = canPlayNatively.lastTruncated;
                    try { URL.revokeObjectURL(rmx.url); } catch (_) {}
                } catch (_) { unhookFfProgress(); }
            }
        }

        // ── Tier 3: transcode (universal floor) ──
        // A caller that can play progressively asks to be handed the decision
        // here instead of waiting for a whole-file encode it may never finish:
        // measured, a 90-minute 1080p film is ~2.3 h AND ~6 GB of output. The
        // flag is opt-in, so every existing caller keeps the old behaviour.
        if (opts.deferTranscode) {
            return _result({
                ok: false, error: 'transcode_deferred', deferred: true,
                meta: base, rich: rich
            });
        }
        onProgress('transcode', 0.4, 'transcode');
        hookFfProgress(0.4, 0.55);
        var txOpts = opts.transcode || {};
        var txMaxW = txOpts.maxWidth != null ? txOpts.maxWidth : 1920;
        var tx = await _transcode(file, audioRel, base.width, txOpts);
        _notResult(opts, tx && tx.blob);
        unhookFfProgress();
        // The caller has to be able to tell the user the picture was reduced —
        // a proxy silently narrower than the source is a silent downgrade.
        if (txMaxW && base.width && base.width > txMaxW) base.scaledTo = txMaxW;
        return _result({
            tier: 'transcode', src: tx.url, meta: base, rich: rich,
            converted: tx.blob, convertedName: _outName(file, 'mp4'),
            decoder: 'browser-native (transcoded)', _urls: [tx.url]
        });
    } catch (err) {
        unhookFfProgress();
        return _result({ ok: false, error: (err && err.message) || 'decode_failed' });
    }
}

function _outName(file, ext) {
    var n = (file && file.name) || 'video';
    var i = n.lastIndexOf('.');
    if (i > 0) n = n.slice(0, i);
    return n + '.' + ext;
}

var API = {
    open: open,
    probeRich: probeRich,
    extractSubtitle: extractSubtitle,
    canPlayNatively: canPlayNatively,
    canPlayAudibly: canPlayAudibly,
    deviceDecodes: _deviceDecodes,
    _streamCodecFromProbe: _streamCodecFromProbe,   // exposed for tests
    _parseLog: _parseLog,          // exposed for tests
    __isStub: false
};
window.UniversalVideoDecode = API;
})();
